import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import { Add, Edit, Delete, Publish, Visibility } from '@mui/icons-material';
import api from '../api';
import Spinner from '../components/Spinner';
import normalizeImageUrl from '../utils/normalizeImageUrl';
import { useStaff } from '../contexts/StaffContext';

interface Question {
  questionText: string;
  questionType: 'multiple-choice' | 'short-answer' | 'essay';
  options: string[];
  correctAnswerIndex: number;
  correctAnswer: string;
  points: number;
  imageUrl?: string;
}

interface Quiz {
  _id: string;
  title: string;
  description: string;
  instructions: string;
  durationMinutes: number;
  attemptsAllowed: number;
  subject: { _id: string; name: string };
  className: string;
  questions: Question[];
  totalPoints: number;
  dueDate: string;
  startTime?: string;
  endTime?: string;
  selectedStudents?: string[];
  isPublished: boolean;
  isVisible: boolean;
  createdAt: string;
}

interface Student {
  _id: string;
  name: string;
  email: string;
  className: string;
}

const StaffQuizzes: React.FC = () => {
  const { staff } = useStaff();
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [subjects, setSubjects] = useState<Array<{ _id: string; name: string }>>([]);
  const [classes, setClasses] = useState<string[]>([]);
  // State for students is required for setStudents in loadClassesAndStudents
  // Removed unused students state
  const [studentsByClass, setStudentsByClass] = useState<{ [key: string]: Student[] }>({});
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState<Quiz | null>(null);
  const [studentSelectionMode, setStudentSelectionMode] = useState<'all' | 'choose'>('all');
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    instructions: string;
    durationMinutes: number;
    attemptsAllowed: number;
    subjectId: string;
    className: string;
    dueDate: string;
    startTime: string;
    endTime: string;
    selectedStudents: string[];
    questions: Question[];
  }>({
    title: '',
    description: '',
    instructions: '',
    durationMinutes: 0,
    attemptsAllowed: 0,
    subjectId: '',
    className: '',
    dueDate: '',
    startTime: '',
    endTime: '',
    selectedStudents: [],
    questions: [{ questionText: '', questionType: 'multiple-choice', options: [''], correctAnswerIndex: 0, correctAnswer: '', points: 1, imageUrl: '' }]
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const formatLocalDatetimeValue = (value?: string | null): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const convertLocalDatetimeToISOString = (localValue: string): string | null => {
    if (!localValue) return null;
    const date = new Date(localValue);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  };

  // Clear cache when staff member logs out or switches accounts
  const clearQuizzesCache = useCallback(() => {
    setQuizzes([]);
    setEditingQuiz(null);
    setError(null);
    setSuccess(null);
    setOpenDialog(false);
  }, []);

  // Clear cache when staff context changes (logout or user switch)
  useEffect(() => {
    if (!staff) {
      clearQuizzesCache();
    }
  }, [staff, clearQuizzesCache]);

  useEffect(() => {
    loadQuizzes();
    loadSubjects();
    loadClassesAndStudents();
  }, []);

  const loadQuizzes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/staff/quizzes');
      setQuizzes(response.data);
    } catch (err: any) {
      setError('Failed to load quizzes');
    } finally {
      setLoading(false);
    }
  };

  const loadSubjects = async () => {
    try {
      const response = await api.get('/staff/subjects');
      setSubjects(response.data);
    } catch {
      setSubjects([]);
    }
  };

  const loadClassesAndStudents = async () => {
    try {
      const response = await api.get('/staff/quizzes-data/classes-and-students');
      const classesData = response.data.classes || [];
      const studentsByClassData = response.data.studentsByClass || {};
      
      setClasses(classesData);
      setStudentsByClass(studentsByClassData);
      
      if (classesData.length === 0) {
        console.warn('No classes available. Make sure students are registered in the system.');
      }
    } catch (err: any) {
      console.error('Error loading classes and students:', err);
      setError(`Failed to load classes: ${err.response?.data?.message || err.message || 'Unknown error'}`);
      setClasses([]);
      setStudentsByClass({});
    }
  };

  const handleOpenDialog = (quiz?: Quiz) => {
    if (quiz) {
      setEditingQuiz(quiz);
      // Determine student selection mode
      const selectedStudentIds = quiz.selectedStudents || [];
      const mode = selectedStudentIds.length === 0 ? 'all' : 'choose';
      setStudentSelectionMode(mode);
      
      setFormData({
        title: quiz.title,
        description: quiz.description,
        instructions: quiz.instructions || '',
        durationMinutes: quiz.durationMinutes || 0,
        attemptsAllowed: quiz.attemptsAllowed || 0,
        subjectId: quiz.subject._id,
        className: quiz.className,
        dueDate: quiz.dueDate?.split('T')[0] || '',
        startTime: formatLocalDatetimeValue(quiz.startTime),
        endTime: formatLocalDatetimeValue(quiz.endTime),
        selectedStudents: selectedStudentIds,
        questions: (quiz.questions || []).map((q: any) => ({
          questionText: q.questionText || '',
          questionType: (q.questionType || 'multiple-choice') as any,
          options: q.options || [],
          correctAnswerIndex: Number.isFinite(q.correctAnswerIndex) ? q.correctAnswerIndex : 0,
          correctAnswer: q.correctAnswer || '',
          points: q.points || 1,
          imageUrl: q.imageUrl || ''
        }))
      });
    } else {
      setEditingQuiz(null);
      setStudentSelectionMode('all');
      setFormData({
        title: '',
        description: '',
        instructions: '',
        durationMinutes: 0,
        attemptsAllowed: 0,
        subjectId: '',
        className: staff?.className || '',
        dueDate: '',
        startTime: '',
        endTime: '',
        selectedStudents: [],
        questions: [{ questionText: '', questionType: 'multiple-choice', options: [''], correctAnswerIndex: 0, correctAnswer: '', points: 1, imageUrl: '' }]
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingQuiz(null);
  };

  const handleAddQuestion = () => {
    setFormData({
      ...formData,
      questions: [
        ...formData.questions,
        { questionText: '', questionType: 'multiple-choice', options: [''], correctAnswerIndex: 0, correctAnswer: '', points: 1, imageUrl: '' }
      ]
    });
  };

  const handleRemoveQuestion = (index: number) => {
    setFormData({
      ...formData,
      questions: formData.questions.filter((_, i) => i !== index)
    });
  };

  const handleQuestionChange = (index: number, field: string, value: any) => {
    const updatedQuestions = [...formData.questions];
    const question = { ...updatedQuestions[index] } as any;

    question[field] = value;

    if (field === 'questionType') {
      if (value === 'multiple-choice') {
        question.options = Array.isArray(question.options) && question.options.length > 0 ? question.options : [''];
        question.correctAnswerIndex = Number.isFinite(question.correctAnswerIndex) ? question.correctAnswerIndex : 0;
      } else if (value === 'short-answer') {
        question.correctAnswer = question.correctAnswer || '';
      } else if (value === 'essay') {
        question.options = [];
        question.correctAnswer = '';
      }
    }

    updatedQuestions[index] = question;
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const handleImageUpload = async (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const uploadData = new FormData();
      uploadData.append('file', file);
      const response = await api.post('/uploads', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const rawUrl = response.data?.data?.fileUrl || response.data?.url;
      if (rawUrl) {
        const normalizedUrl = normalizeImageUrl(rawUrl) || rawUrl;
        handleQuestionChange(index, 'imageUrl', normalizedUrl);
        setSuccess('Image uploaded successfully');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setLoading(false);
    }
  };

  const handleOptionChange = (questionIndex: number, optionIndex: number, value: string) => {
    const updatedQuestions = [...formData.questions];
    const question = { ...updatedQuestions[questionIndex] } as any;
    const options = Array.isArray(question.options) ? [...question.options] : [];
    options[optionIndex] = value;
    question.options = options;
    updatedQuestions[questionIndex] = question;
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const handleAddOption = (questionIndex: number) => {
    const updatedQuestions = [...formData.questions];
    const question = { ...updatedQuestions[questionIndex] } as any;
    question.options = Array.isArray(question.options) ? [...question.options, ''] : [''];
    updatedQuestions[questionIndex] = question;
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const handleRemoveOption = (questionIndex: number, optionIndex: number) => {
    const updatedQuestions = [...formData.questions];
    const question = { ...updatedQuestions[questionIndex] } as any;
    const options = Array.isArray(question.options) ? question.options.filter((_: any, i: number) => i !== optionIndex) : [];
    question.options = options.length > 0 ? options : [''];
    if (question.correctAnswerIndex >= options.length) {
      question.correctAnswerIndex = 0;
    }
    updatedQuestions[questionIndex] = question;
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (!formData.title || !formData.subjectId || formData.questions.length === 0) {
        setError('Title, subject, and at least one question are required');
        return;
      }

      const payload = {
        title: formData.title,
        description: formData.description,
        instructions: formData.instructions,
        durationMinutes: formData.durationMinutes,
        attemptsAllowed: formData.attemptsAllowed,
        subjectId: formData.subjectId,
        className: formData.className,
        dueDate: formData.dueDate || null,
        startTime: convertLocalDatetimeToISOString(formData.startTime),
        endTime: convertLocalDatetimeToISOString(formData.endTime),
        selectedStudents: studentSelectionMode === 'all' ? [] : formData.selectedStudents,
        questions: formData.questions,
        totalPoints: formData.questions.reduce((sum, q) => sum + q.points, 0)
      };

      if (editingQuiz) {
        await api.put(`/staff/quizzes/${editingQuiz._id}`, payload);
        setSuccess('Quiz updated successfully');
      } else {
        await api.post('/staff/quizzes', payload);
        setSuccess('Quiz created successfully');
      }

      handleCloseDialog();
      loadQuizzes();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save quiz');
    }
  };

  const handlePublish = async (quizId: string, currentStatus: boolean) => {
    try {
      await api.put(`/staff/quizzes/${quizId}/publish`, { isPublished: !currentStatus });
      setSuccess(`Quiz ${!currentStatus ? 'published' : 'unpublished'} successfully`);
      loadQuizzes();
    } catch (err: any) {
      setError('Failed to update quiz status');
    }
  };

  const handleDelete = async (quizId: string) => {
    if (window.confirm('Are you sure you want to delete this quiz?')) {
      try {
        await api.delete(`/staff/quizzes/${quizId}`);
        setSuccess('Quiz deleted successfully');
        loadQuizzes();
      } catch (err: any) {
        setError('Failed to delete quiz');
      }
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Student Quizzes</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Create Quiz
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Title</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Subject</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Questions</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Points</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {quizzes.length > 0 ? (
              quizzes.map((quiz) => (
                <TableRow key={quiz._id}>
                  <TableCell sx={{ color: 'text.primary' }}>{quiz.title}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>{quiz.subject?.name}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>{quiz.questions.length}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>{quiz.totalPoints}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>
                    <Chip
                      label={quiz.isPublished ? 'Published' : 'Draft'}
                      color={quiz.isPublished ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handlePublish(quiz._id, quiz.isPublished)}
                      title={quiz.isPublished ? 'Unpublish' : 'Publish'}
                    >
                      {quiz.isPublished ? <Publish fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenDialog(quiz)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(quiz._id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                  No quizzes created yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingQuiz ? 'Edit Quiz' : 'Create New Quiz'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Quiz Title"
            fullWidth
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Subject</InputLabel>
            <Select
              value={formData.subjectId}
              label="Subject"
              onChange={(e) => setFormData({ ...formData, subjectId: e.target.value })}
            >
              {subjects.map((subject) => (
                <MenuItem key={subject._id} value={subject._id}>
                  {subject.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Class</InputLabel>
            <Select
              value={formData.className}
              label="Class"
              onChange={(e) => {
                const newClass = e.target.value;
                setFormData({ ...formData, className: newClass, selectedStudents: [] });
                setStudentSelectionMode('all');
              }}
            >
              {classes.length > 0 ? (
                classes.map((className) => (
                  <MenuItem key={className} value={className}>
                    {className}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  No classes available
                </MenuItem>
              )}
            </Select>
            {classes.length === 0 && (
              <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
                Note: No classes found. Ensure students are registered in the system.
              </Typography>
            )}
          </FormControl>
          <TextField
            label="Due Date"
            type="date"
            fullWidth
            value={formData.dueDate}
            onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Start Time"
            type="datetime-local"
            fullWidth
            value={formData.startTime}
            onChange={(e) => {
              const startTime = e.target.value;
              setFormData({ ...formData, startTime });
              // Auto-calculate duration if endTime exists
              if (formData.endTime && startTime) {
                const start = new Date(startTime).getTime();
                const end = new Date(formData.endTime).getTime();
                if (end > start) {
                  const durationMinutes = Math.floor((end - start) / 60000);
                  setFormData(prev => ({ ...prev, durationMinutes }));
                }
              }
            }}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            helperText="Quiz becomes available at this time"
          />
          <TextField
            label="End Time"
            type="datetime-local"
            fullWidth
            value={formData.endTime}
            onChange={(e) => {
              const endTime = e.target.value;
              setFormData({ ...formData, endTime });
              // Auto-calculate duration if startTime exists
              if (formData.startTime && endTime) {
                const start = new Date(formData.startTime).getTime();
                const end = new Date(endTime).getTime();
                if (end > start) {
                  const durationMinutes = Math.floor((end - start) / 60000);
                  setFormData(prev => ({ ...prev, durationMinutes }));
                }
              }
            }}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            helperText="Quiz closes at this time"
          />
          
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Student Eligibility
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <Stack direction="row" spacing={2}>
                <Button
                  variant={studentSelectionMode === 'all' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setStudentSelectionMode('all');
                    setFormData({ ...formData, selectedStudents: [] });
                  }}
                  size="small"
                >
                  All Students in Class
                </Button>
                <Button
                  variant={studentSelectionMode === 'choose' ? 'contained' : 'outlined'}
                  onClick={() => setStudentSelectionMode('choose')}
                  disabled={!formData.className}
                  size="small"
                >
                  Choose Specific Students
                </Button>
              </Stack>
            </FormControl>
          </Box>

          {studentSelectionMode === 'choose' && formData.className && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#f0f0f0', borderRadius: 1, border: '2px solid #ddd' }}>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold', color: '#000' }}>
                Select eligible students from {formData.className}:
              </Typography>
              <Box sx={{ maxHeight: 300, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(studentsByClass[formData.className] || []).length > 0 ? (
                  (studentsByClass[formData.className] || []).map((student) => (
                    <Box
                      key={student._id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        backgroundColor: 'white',
                        borderRadius: 1,
                        border: '1px solid #ccc',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: '#f9f9f9',
                          borderColor: '#999'
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedStudents.includes(student._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              selectedStudents: [...formData.selectedStudents, student._id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              selectedStudents: formData.selectedStudents.filter(id => id !== student._id)
                            });
                          }
                        }}
                        style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#000' }}>{student.name}</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666' }}>{student.email}</Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: '#999', p: 2, textAlign: 'center' }}>
                    No students found in this class
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          <TextField
            label="Quiz Instructions"
            fullWidth
            multiline
            rows={3}
            value={formData.instructions}
            onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
            margin="normal"
            helperText="Instructions shown to students before they start the quiz."
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <TextField
              label="Duration (minutes)"
              type="number"
              fullWidth
              value={formData.durationMinutes}
              onChange={(e) => setFormData({ ...formData, durationMinutes: Math.max(0, Number(e.target.value)) })}
              margin="normal"
              InputProps={{ inputProps: { min: 0 } }}
            />
            <TextField
              label="Attempts Allowed"
              type="number"
              fullWidth
              value={formData.attemptsAllowed}
              onChange={(e) => setFormData({ ...formData, attemptsAllowed: Math.max(0, Number(e.target.value)) })}
              margin="normal"
              helperText="Set 0 for unlimited attempts."
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Box>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Questions</Typography>
          {formData.questions.map((question, index) => (
            <Card key={index} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle2">Question {index + 1}</Typography>
                  {formData.questions.length > 1 && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveQuestion(index)}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
                <TextField
                  label="Question Text"
                  fullWidth
                  multiline
                  rows={2}
                  value={question.questionText}
                  onChange={(e) => handleQuestionChange(index, 'questionText', e.target.value)}
                  margin="normal"
                />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                  <TextField
                    label="Image URL (Optional)"
                    fullWidth
                    value={question.imageUrl || ''}
                    onChange={(e) => handleQuestionChange(index, 'imageUrl', e.target.value)}
                    size="small"
                  />
                  <Button variant="outlined" component="label" sx={{ whiteSpace: 'nowrap' }}>
                    Upload Image
                    <input type="file" hidden accept="image/*" onChange={(e) => handleImageUpload(index, e)} />
                  </Button>
                </Box>
                {question.imageUrl && (
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <img src={normalizeImageUrl(question.imageUrl) || question.imageUrl} alt="Question preview" style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }} />
                  </Box>
                )}
                <FormControl fullWidth margin="normal">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={question.questionType}
                    label="Type"
                    onChange={(e) => handleQuestionChange(index, 'questionType', e.target.value)}
                  >
                    <MenuItem value="multiple-choice">Multiple Choice</MenuItem>
                    <MenuItem value="short-answer">Short Answer</MenuItem>
                    <MenuItem value="essay">Essay</MenuItem>
                  </Select>
                </FormControl>

                {question.questionType === 'multiple-choice' && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Options</Typography>
                    <Stack spacing={1}>
                      {question.options.map((option, optionIndex) => (
                        <Box key={optionIndex} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            label={`Option ${optionIndex + 1}`}
                            fullWidth
                            value={option}
                            onChange={(e) => handleOptionChange(index, optionIndex, e.target.value)}
                          />
                          {question.options.length > 1 && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleRemoveOption(index, optionIndex)}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                      ))}
                    </Stack>
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => handleAddOption(index)}
                    >
                      Add Option
                    </Button>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Correct Option</InputLabel>
                      <Select
                        value={question.correctAnswerIndex}
                        label="Correct Option"
                        onChange={(e) => handleQuestionChange(index, 'correctAnswerIndex', Number(e.target.value))}
                      >
                        {question.options.map((option, optionIndex) => (
                          <MenuItem key={optionIndex} value={optionIndex}>
                            {`Option ${optionIndex + 1}${option ? `: ${option}` : ''}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {question.questionType === 'short-answer' && (
                  <TextField
                    label="Expected Answer"
                    fullWidth
                    multiline
                    rows={2}
                    value={question.correctAnswer}
                    onChange={(e) => handleQuestionChange(index, 'correctAnswer', e.target.value)}
                    margin="normal"
                    helperText="Students' short-answer responses will be matched against this answer."
                  />
                )}

                {question.questionType === 'essay' && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Essay questions will require manual grading after submission.
                  </Typography>
                )}

                <TextField
                  label="Points"
                  type="number"
                  fullWidth
                  value={question.points}
                  onChange={(e) => handleQuestionChange(index, 'points', parseInt(e.target.value))}
                  margin="normal"
                />
              </CardContent>
            </Card>
          ))}

          <Button
            variant="outlined"
            fullWidth
            onClick={handleAddQuestion}
            sx={{ mb: 2 }}
          >
            Add Question
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingQuiz ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffQuizzes;
